import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment, endpoints } from '@environment';
import { HouseholdMember, LanguageCode, Profile, UpdateLanguagePreferenceRequest, UserLanguagePreference } from '@core/models';
import { suppressGlobalErrorFeedback } from '@core/interceptors';

interface DeleteAccountRequest {
  code: string;
  app?: string;
}

interface ConfirmDeleteRequest {
  app?: string;
  twoFactorAuthorizationToken?: string;
}

interface PasswordChangeCodeRequest {
  applicationId?: string;
  twoFactorAuthorizationToken?: string;
}

interface PasswordChangeCodeResponse {
  email: string;
}

interface ChangePasswordRequest {
  passwordChangeCode: string;
  newPassword: string;
  currentUserSessionId?: string;
}

interface EmailChangeCodeRequest {
  newEmail: string;
  currentPassword: string;
  applicationId?: string;
  twoFactorAuthorizationToken?: string;
}

interface EmailChangeCodeResponse {
  email: string;
}

interface ChangeEmailRequest {
  emailChangeCode: string;
  currentUserSessionId?: string;
}

@Injectable({ providedIn: 'root' })
export class ProfileService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.authApiUrl}/${endpoints.auth.v1.profile}`;

  getProfile(): Observable<Profile> {
    return this.http.get<Profile>(this.baseUrl, { context: suppressGlobalErrorFeedback() });
  }

  updateLanguage(languageCode: LanguageCode): Observable<Profile | UserLanguagePreference> {
    const body: UpdateLanguagePreferenceRequest = { languageCode };
    return this.http.put<Profile | UserLanguagePreference>(
      `${environment.apiUrl}/${endpoints.auth.v1.profileLanguage}`,
      body,
      { context: suppressGlobalErrorFeedback() }
    );
  }

  confirmDelete(app?: string, twoFactorAuthorizationToken?: string): Observable<void> {
    const body: ConfirmDeleteRequest = {};

    if (app) body.app = app;
    if (twoFactorAuthorizationToken) body.twoFactorAuthorizationToken = twoFactorAuthorizationToken;

    return this.http.post<void>(
      `${environment.authApiUrl}/${endpoints.auth.v1.profileConfirmDelete}`,
      body,
      { context: suppressGlobalErrorFeedback() }
    );
  }

  sendPasswordChangeCode(applicationId?: string, twoFactorAuthorizationToken?: string): Observable<PasswordChangeCodeResponse> {
    const body: PasswordChangeCodeRequest = {};

    if (applicationId) {
      body.applicationId = applicationId;
    }
    if (twoFactorAuthorizationToken) body.twoFactorAuthorizationToken = twoFactorAuthorizationToken;

    return this.http.post<PasswordChangeCodeResponse>(
      `${environment.authApiUrl}/${endpoints.auth.v1.profilePasswordChangeCode}`,
      body,
      { context: suppressGlobalErrorFeedback() }
    );
  }

  changePassword(request: ChangePasswordRequest): Observable<void> {
    return this.http.post<void>(
      `${environment.authApiUrl}/${endpoints.auth.v1.profileChangePassword}`,
      request,
      { context: suppressGlobalErrorFeedback() }
    );
  }

  sendEmailChangeCode(request: EmailChangeCodeRequest): Observable<EmailChangeCodeResponse> {
    return this.http.post<EmailChangeCodeResponse>(
      `${environment.authApiUrl}/${endpoints.auth.v1.profileEmailChangeCode}`,
      request,
      { context: suppressGlobalErrorFeedback() }
    );
  }

  changeEmail(request: ChangeEmailRequest): Observable<Profile> {
    return this.http.post<Profile>(
      `${environment.authApiUrl}/${endpoints.auth.v1.profileChangeEmail}`,
      request,
      { context: suppressGlobalErrorFeedback() }
    );
  }

  syncSpendKeepProfileEmail(email: string): Observable<HouseholdMember> {
    return this.http.put<HouseholdMember>(
      `${environment.apiUrl}/${endpoints.v1.profileEmail}`,
      { email },
      { context: suppressGlobalErrorFeedback() }
    );
  }

  deleteAccount(code: string, app?: string): Observable<void> {
    const body: DeleteAccountRequest = { code };

    if (app) {
      body.app = app;
    }

    return this.http.post<void>(
      `${environment.authApiUrl}/${endpoints.auth.v1.profileDelete}`,
      body,
      { context: suppressGlobalErrorFeedback() }
    );
  }
}
